import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import AppTransition from '../../../components/appComponents/AppTransition';
import EditMyBump from './editMyBump';
import ValueAddMyBump from './valueaddMyBump';
import './viewDoneBump.css';

function ViewDoneBump() {
  const user = auth.currentUser;
  const { videoId } = useParams();
  const navigate = useNavigate();

  const [bump, setBump] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  // One live listener on the bump doc drives the whole page
  useEffect(() => {
    if (!user?.uid) return;
    const videoDocRef = doc(db, 'users', user.uid, 'bumpContent', `bump-${videoId}`);
    return onSnapshot(
      videoDocRef,
      (snap) => {
        if (!snap.exists()) {
          navigate('/404-bumper');
          return;
        }
        setBump(snap.data());
      },
      () => {}
    );
  }, [videoId, user, navigate]);

  useEffect(() => {
    if (!user?.uid) return;
    const accessDocRef = doc(db, 'users', user.uid, 'contentAccess', 'youtubeAccess');
    return onSnapshot(
      accessDocRef,
      (snap) => {
        if (snap.exists()) setAccessToken(snap.data().accessToken || null);
      },
      () => {}
    );
  }, [user]);

  // Derived once per snapshot so child props keep a stable identity between renders
  const video = useMemo(() => {
    const d = bump || {};
    return {
      videoTitle: d.video_file_name || '',
      videoQuality: d.video_quality || '',
      summary: d.summary || '',
      takeaways: d.takeaways || [],
      hashtags: d.hashtags || [],
      timestamps: d.timestamps || [],
      titles: d.titles || [],
      videoDuration: d.video_duration_in_seconds || 0,
      videoSize: d.video_blob_size_mb || 0,
      isProcessedbyAI: !!(d.summary || d.takeaways || d.hashtags || d.timestamps || d.titles),
      hasTimestamps: !!d.timestamps,
      isTranscriptReady: !!d.is_transcript_ready,
      isTranscriptEmpty: !!d.is_transcript_empty,
      isYouTubePosted: !!d.is_youtube_posted,
    };
  }, [bump]);

  return (
    <div className="viewdonebump-uploaddashboard">
      <AppTransition>
        <div className="viewdonebump-page">
          {bump && <Helmet><title>{`Bumpups | ${bump.publish_title || video.videoTitle || 'Video'}`}</title></Helmet>}
          <Link to="/bumper" className="viewdonebump-back">
            <FontAwesomeIcon icon={faArrowLeft} />
            Creator Studio
          </Link>
          <EditMyBump
            videoId={videoId}
            user={user}
            accessToken={accessToken}
            isTranscriptReady={video.isTranscriptReady}
            isTranscriptEmpty={video.isTranscriptEmpty}
            isProcessedbyAI={video.isProcessedbyAI}
            isYouTubePosted={video.isYouTubePosted}
            videoTitle={video.videoTitle}
            videoQuality={video.videoQuality}
            summary={video.summary}
            takeaways={video.takeaways}
            hashtags={video.hashtags}
            timestamps={video.timestamps}
            videoDuration={video.videoDuration}
            videoSize={video.videoSize}
          />
          {bump && (
            <ValueAddMyBump
              videoId={videoId}
              user={user}
              isProcessedbyAI={video.isProcessedbyAI}
              isTranscriptEmpty={video.isTranscriptEmpty}
              hasTimestamps={video.hasTimestamps}
              summary={video.summary}
              titles={video.titles}
              takeaways={video.takeaways}
              hashtags={video.hashtags}
              timestamps={video.timestamps}
            />
          )}
        </div>
      </AppTransition>
    </div>
  );
}

export default ViewDoneBump;
